import { isAxiosError } from 'axios';
import { generatePath } from 'react-router-dom';
import { suitabilityAxiosClient } from 'libs/axios';
import {
  clientSuitabilityTests,
  suitabilityPreview,
  suitabilityQuestionnaire,
} from '../constants/urls';
import type {
  CreateSuitabilityTestRequest,
  CreateSuitabilityTestResponse,
  PreviewSuitabilityResponse,
  QuestionnaireContentResponse,
} from './interfaces';

/** Idioma base del catálogo; el front cae a él si el EP no tiene el pedido. */
const DEFAULT_CONTENT_LANGUAGE = 'es';

const fetchQuestionnaireContent = async (
  language: string,
): Promise<QuestionnaireContentResponse> => {
  const { data } =
    await suitabilityAxiosClient.get<QuestionnaireContentResponse>(
      suitabilityQuestionnaire,
      { params: { language } },
    );
  return data;
};

/**
 * Textos del cuestionario vigente (versión + idioma) para pintar el wizard.
 *
 * El front pide el idioma del navegador. El EP es estricto (404 si no hay contenido
 * en ese idioma), así que el fallback de idioma lo hace aquí: si responde 404 y el
 * idioma pedido no era ya 'es', reintenta en 'es'. Cualquier otro error (o un 404
 * pidiendo ya 'es') se propaga → el wizard muestra el banner de error.
 */
export const getQuestionnaireContent = async (
  language: string,
): Promise<QuestionnaireContentResponse> => {
  try {
    return await fetchQuestionnaireContent(language);
  } catch (error) {
    const isMissingLanguage =
      isAxiosError(error) && error.response?.status === 404;
    if (!isMissingLanguage || language === DEFAULT_CONTENT_LANGUAGE) throw error;

    return fetchQuestionnaireContent(DEFAULT_CONTENT_LANGUAGE);
  }
};

/** Calculate the result without persisting (wizard live preview). */
export const previewSuitability = async (
  answers: Record<string, unknown>,
  questionnaireVersion: string,
): Promise<PreviewSuitabilityResponse> => {
  const { data } =
    await suitabilityAxiosClient.post<PreviewSuitabilityResponse>(
      suitabilityPreview,
      { answers, questionnaireVersion },
    );
  return data;
};

/**
 * Persiste el test del cliente. El PDF que el asesor revisó viaja en el propio body
 * JSON como `request.pdf` (base64); el backend recibe el Create en [FromBody] con
 * `Answers` (JObject), `QuestionnaireVersion` y `Pdf` (base64).
 */
export const createSuitabilityTest = async (
  clientId: string,
  request: CreateSuitabilityTestRequest,
): Promise<CreateSuitabilityTestResponse> => {
  const { data } =
    await suitabilityAxiosClient.post<CreateSuitabilityTestResponse>(
      generatePath(clientSuitabilityTests, { clientId }),
      request,
    );
  return data;
};
